import React, { useState, useRef, useEffect } from 'react';
import {
  MoreVertical,
  MessageSquare,
  Activity,
  Calendar,
  UserX,
  XCircle,
  Printer,
  Share2,
  RotateCcw,
} from 'lucide-react';
import { Button } from '../common/Button';

interface AppointmentOptionsMenuProps {
  status: 'booked' | 'completed' | 'no_show' | 'cancelled';
  onAddNote?: () => void;
  onViewActivity?: () => void;
  onReschedule?: () => void;
  onMarkNoShow?: () => void;
  onCancel?: () => void;
  onPrintReceipt?: () => void;
  onShareReceipt?: () => void;
  onRefund?: () => void;
}

export function AppointmentOptionsMenu({
  status,
  onAddNote,
  onViewActivity,
  onReschedule,
  onMarkNoShow,
  onCancel,
  onPrintReceipt,
  onShareReceipt,
  onRefund,
}: AppointmentOptionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isCompleted = status === 'completed';

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <Button
        variant="secondary"
        size="md"
        icon={MoreVertical}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Opciones rápidas"
        className="border-border hover:border-brand-sky hover:text-brand-sky transition-colors"
      >
        Opciones
      </Button>

      {isOpen && (
        <div className="absolute left-0 bottom-full mb-2 w-60 rounded-2xl bg-surface border border-border/80 shadow-2xl z-50 overflow-hidden backdrop-blur-xl animate-fade-in divide-y divide-border/40">
          <div className="p-1 space-y-0.5">
            <button
              onClick={() => {
                setIsOpen(false);
                onAddNote?.();
              }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-text-primary hover:bg-surface-secondary/70 rounded-xl transition-colors text-left"
            >
              <MessageSquare className="w-4 h-4 text-brand-sky" />
              <span>Añadir nota / comentario</span>
            </button>

            <button
              onClick={() => {
                setIsOpen(false);
                onViewActivity?.();
              }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-text-primary hover:bg-surface-secondary/70 rounded-xl transition-colors text-left"
            >
              <Activity className="w-4 h-4 text-brand-orange" />
              <span>Ver actividad de la cita</span>
            </button>
          </div>

          {!isCompleted ? (
            <div className="p-1 space-y-0.5">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onReschedule?.();
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-text-primary hover:bg-surface-secondary/70 rounded-xl transition-colors text-left"
              >
                <Calendar className="w-4 h-4 text-emerald-500" />
                <span>Reprogramar / Reagendar</span>
              </button>

              <button
                onClick={() => {
                  setIsOpen(false);
                  onMarkNoShow?.();
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-amber-500 hover:bg-amber-500/10 rounded-xl transition-colors text-left"
              >
                <UserX className="w-4 h-4 text-amber-500" />
                <span>Marcar Inasistencia (No-Show)</span>
              </button>

              <button
                onClick={() => {
                  setIsOpen(false);
                  onCancel?.();
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-rose-500 hover:bg-rose-500/10 rounded-xl transition-colors text-left"
              >
                <XCircle className="w-4 h-4 text-rose-500" />
                <span>Cancelar cita</span>
              </button>
            </div>
          ) : (
            <div className="p-1 space-y-0.5">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onPrintReceipt?.();
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-text-primary hover:bg-surface-secondary/70 rounded-xl transition-colors text-left"
              >
                <Printer className="w-4 h-4 text-brand-sky" />
                <span>Imprimir comprobante</span>
              </button>

              <button
                onClick={() => {
                  setIsOpen(false);
                  onShareReceipt?.();
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-text-primary hover:bg-surface-secondary/70 rounded-xl transition-colors text-left"
              >
                <Share2 className="w-4 h-4 text-emerald-500" />
                <span>Enviar recibo por WhatsApp</span>
              </button>

              <button
                onClick={() => {
                  setIsOpen(false);
                  onRefund?.();
                }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-rose-500 hover:bg-rose-500/10 rounded-xl transition-colors text-left"
              >
                <RotateCcw className="w-4 h-4 text-rose-500" />
                <span>Solicitar Reembolso / Devolución</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
